import React, { useState, useEffect, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import GenericFormDrawer from '../../../components/GenericFormDrawer';
import GenericSelect from '../../../components/ui/GenericSelect';
import CheckboxInput from '../../../components/ui/CheckboxInput';
import { previewLeaveReconciliationBulkJob } from '../../../services/leaveReconciliationService';
import {
  BULK_KIND, BULK_SCOPE, buildBulkRequest, estimateParts, isYearOpen,
} from '../config/bulk';

// Toplu işi BAŞLATAN çekmece — ZIP paketi ya da çalışanlara bildirim. Bir nesne
// oluşturduğu için GenericFormDrawer kabuğudur (UI altın kuralı: modal değil);
// kabuk yalnız başlık/düğme/overlay'i verir (schema=[]), içerik sayfaya özgüdür
// (NewSessionDrawer/GoalTemplatesTab deseni).
//
// ÖNİZLEME: çekmece açıkken ve her seçenek değişiminde sunucudan "kaç kişi
// işlenecek, kaçı neden atlanacak" dökümü alınır. İstek gövdesi başlatmayla
// BİREBİR aynıdır (buildBulkRequest) — İK'nın gördüğü sayı başlattığı işin sayısıdır.

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Notice = styled.div`
  font-size: 13px;
  line-height: 1.5;
  border-radius: 6px;
  padding: 10px 12px;
  background: ${({ $tone }) => ($tone === 'danger' ? '#FEF2F2' : $tone === 'warn' ? '#FEFCE8' : '#F5F7F9')};
  border: 1px solid ${({ $tone }) => ($tone === 'danger' ? '#FECACA' : $tone === 'warn' ? '#FDE68A' : '#E8ECF0')};
  color: ${({ $tone }) => ($tone === 'danger' ? '#B3261E' : $tone === 'warn' ? '#854D0E' : '#475569')};
`;

const Option = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Hint = styled.div`
  font-size: 12px;
  line-height: 1.45;
  color: ${({ theme }) => theme.colors.text.dark.little};
`;

const SectionTitle = styled.div`
  margin-top: 6px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${({ theme }) => theme.colors.text.dark.little};
`;

const Rows = styled.dl`
  margin: 0;
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 8px 16px;
`;

const Label = styled.dt`
  margin: 0;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.dark.little};
  padding-left: ${({ $indent }) => ($indent ? '14px' : '0')};
`;

const Value = styled.dd`
  margin: 0;
  font-size: 13px;
  text-align: right;
  font-weight: ${({ $strong }) => ($strong ? 700 : 400)};
  color: ${({ theme }) => theme.colors.text.primary};
`;

export default function BulkStartDrawer({
  kind, open, onClose, year, selectedIds, status, departmentId, onStart,
}) {
  const { t } = useTranslation();
  const selectedCount = selectedIds.length;
  const isNotify = kind === BULK_KIND.NOTIFY;

  // null = çekmece kapalı / henüz kurulmadı. Önizleme kapsam kurulmadan İSTENMEZ —
  // aksi hâlde her açılışta önce eski kapsamla boşa bir sorgu giderdi (büyük bir
  // kiracıda önizleme tüm listeyi çözer, ucuz değildir).
  const [scope, setScope] = useState(null);
  const [regenerateGenerated, setRegenerateGenerated] = useState(false);
  const [skipAlreadyNotified, setSkipAlreadyNotified] = useState(true);
  const [preview, setPreview] = useState({ loading: false, data: null, error: null });

  // Her açılış temiz başlar: seçim varsa kapsam "seçili", yoksa "listedeki herkes".
  // Tehlikeli seçenek (yeniden üretim) hiçbir zaman önceki açılıştan TAŞINMAZ.
  useEffect(() => {
    if (!open) {
      setScope(null);
      return;
    }
    setScope(selectedCount > 0 ? BULK_SCOPE.SELECTED : BULK_SCOPE.FILTER);
    setRegenerateGenerated(false);
    setSkipAlreadyNotified(true);
  }, [open, selectedCount]);

  const request = useMemo(() => buildBulkRequest({
    kind, year, scope, selectedIds, status, departmentId, regenerateGenerated, skipAlreadyNotified,
  }), [kind, year, scope, selectedIds, status, departmentId, regenerateGenerated, skipAlreadyNotified]);

  useEffect(() => {
    if (!open || !scope) return undefined;
    let cancelled = false;
    setPreview({ loading: true, data: null, error: null });
    previewLeaveReconciliationBulkJob(request)
      .then((res) => {
        if (!cancelled) setPreview({ loading: false, data: res.data?.data || null, error: null });
      })
      .catch((err) => {
        if (!cancelled) {
          setPreview({ loading: false, data: null, error: err?.response?.data?.message || 'unknown' });
        }
      });
    return () => { cancelled = true; };
  }, [open, scope, request]);

  const willProcess = preview.data?.willProcess || 0;
  const canStart = !preview.loading && !preview.error && willProcess > 0;

  const handleSave = useCallback(async () => {
    if (!canStart) return false;
    return onStart(request);
  }, [canStart, onStart, request]);

  const scopeOptions = useMemo(() => ([
    ...(selectedCount > 0
      ? [{ id: BULK_SCOPE.SELECTED, name: t('leaveReconciliation.bulk.scopeSelected', { count: selectedCount }) }]
      : []),
    { id: BULK_SCOPE.FILTER, name: t('leaveReconciliation.bulk.scopeFilter') },
  ]), [t, selectedCount]);

  const estimate = useMemo(() => {
    const { key, ...params } = estimateParts(preview.data?.estimatedSeconds);
    return t(`leaveReconciliation.bulk.estimate.${key}`, params);
  }, [t, preview.data]);

  const errorText = preview.error && preview.error !== 'unknown'
    ? t(`responseErrors.${preview.error}`, { defaultValue: t('leaveReconciliation.bulk.previewFailed') })
    : t('leaveReconciliation.bulk.previewFailed');

  const d = preview.data;

  return (
    <GenericFormDrawer
      open={open}
      onClose={onClose}
      title={t(isNotify ? 'leaveReconciliation.bulk.notifyTitle' : 'leaveReconciliation.bulk.zipTitle', { year })}
      schema={[]}
      onSave={handleSave}
      submitLabel={t('leaveReconciliation.bulk.start')}
      submitDisabled={!canStart}
    >
      <Stack data-role="bulk-start">
        <Notice $tone="info">
          {t(isNotify ? 'leaveReconciliation.bulk.notifyHint' : 'leaveReconciliation.bulk.zipHint')}
        </Notice>
        {isYearOpen(year) && (
          <Notice $tone="warn" data-role="bulk-year-open">
            {t('leaveReconciliation.bulk.yearNotFinished', { year })}
          </Notice>
        )}

        <Option>
          <GenericSelect
            label={t('leaveReconciliation.bulk.scope')}
            options={scopeOptions}
            value={scope}
            clearable={false}
            onChange={(val) => { if (val) setScope(val); }}
          />
          <Hint>
            {scope === BULK_SCOPE.SELECTED
              ? t('leaveReconciliation.bulk.scopeSelectedHint')
              : t('leaveReconciliation.bulk.scopeFilterHint')}
          </Hint>
        </Option>

        <Option>
          <CheckboxInput
            data-role="bulk-regenerate"
            label={t('leaveReconciliation.bulk.regenerateGenerated')}
            checked={regenerateGenerated}
            onChange={setRegenerateGenerated}
          />
          <Hint>{t('leaveReconciliation.bulk.regenerateGeneratedHint')}</Hint>
        </Option>

        {isNotify && (
          <Option>
            <CheckboxInput
              data-role="bulk-skip-notified"
              label={t('leaveReconciliation.bulk.skipAlreadyNotified')}
              checked={skipAlreadyNotified}
              onChange={setSkipAlreadyNotified}
            />
            <Hint>{t('leaveReconciliation.bulk.skipAlreadyNotifiedHint')}</Hint>
          </Option>
        )}

        <SectionTitle>{t('leaveReconciliation.bulk.previewTitle')}</SectionTitle>

        {preview.loading && <Hint>{t('common.loading')}</Hint>}

        {!preview.loading && preview.error && (
          <Notice $tone="danger" data-role="bulk-preview-error">{errorText}</Notice>
        )}

        {!preview.loading && d && (
          <Rows data-role="bulk-preview">
            <Label>{t('leaveReconciliation.bulk.previewTotal')}</Label>
            <Value>{d.total}</Value>

            <Label>{t('leaveReconciliation.bulk.previewWillProcess')}</Label>
            <Value $strong data-role="bulk-will-process">{d.willProcess}</Value>
            {d.draft > 0 && (
              <>
                <Label $indent>{t('leaveReconciliation.bulk.previewDraft')}</Label>
                <Value>{d.draft}</Value>
              </>
            )}
            {d.generated > 0 && (
              <>
                <Label $indent>
                  {regenerateGenerated
                    ? t('leaveReconciliation.bulk.previewGeneratedRegenerate')
                    : t('leaveReconciliation.bulk.previewGeneratedKeep')}
                </Label>
                <Value>{d.generated}</Value>
              </>
            )}

            {d.delivered > 0 && (
              <>
                <Label>{t('leaveReconciliation.bulk.previewDelivered')}</Label>
                <Value>{d.delivered}</Value>
              </>
            )}
            {isNotify && d.noActiveAccount > 0 && (
              <>
                <Label>{t('leaveReconciliation.bulk.previewNoActiveAccount')}</Label>
                <Value>{d.noActiveAccount}</Value>
              </>
            )}
            {isNotify && d.noEmail > 0 && (
              <>
                <Label>{t('leaveReconciliation.bulk.previewNoEmail')}</Label>
                <Value>{d.noEmail}</Value>
              </>
            )}
            {isNotify && d.alreadyNotified > 0 && (
              <>
                <Label>{t('leaveReconciliation.bulk.previewAlreadyNotified')}</Label>
                <Value>{d.alreadyNotified}</Value>
              </>
            )}

            {d.willProcess > 0 && (
              <>
                <Label>{t('leaveReconciliation.bulk.previewEstimate')}</Label>
                <Value data-role="bulk-estimate">{estimate}</Value>
              </>
            )}
          </Rows>
        )}

        {!preview.loading && d && d.willProcess === 0 && (
          <Notice $tone="warn" data-role="bulk-nothing">{t('leaveReconciliation.bulk.nothingToProcess')}</Notice>
        )}
      </Stack>
    </GenericFormDrawer>
  );
}
